import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { setToken, clearToken, getToken } from '../lib/api';

const AuthCtx = createContext(null);

/**
 * Entitlement/access is ALWAYS decided by the production backend — the frontend never
 * computes it independently. We read the backend's own signals:
 *   - GET /api/auth/me      -> user (is_admin, is_premium, subscription_status)
 *   - GET /api/entitlements -> { STOCK_METHOD_PREMIUM } (Whop Full Access, server-resolved)
 * and mirror exactly the backend's premium rule (Stripe active | admin | Whop entitled).
 */
function deriveAccess(user, entitlements) {
  if (!user) return false;
  if (entitlements && entitlements.STOCK_METHOD_PREMIUM === true) return true; // Whop path
  if (user.is_admin === true) return true; // admin path (matches backend content gate)
  if (user.is_premium === true && ['active', 'trialing'].includes(user.subscription_status)) return true; // Stripe path
  return false;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [access, setAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setEntitlements(null);
      setAccess(false);
      setLoading(false);
      return;
    }
    try {
      const meRes = await api.get('/auth/me');
      const u = meRes.data.user || meRes.data;
      let ent = null;
      try {
        const entRes = await api.get('/entitlements');
        ent = entRes.data;
      } catch (e) {
        ent = null; // entitlements optional; access can still come from admin/stripe
      }
      setUser(u);
      setEntitlements(ent);
      setAccess(deriveAccess(u, ent));
    } catch (e) {
      // Only sign out on a DEFINITIVE auth failure (401). Transient network / 5xx
      // errors must NOT log the user out — keep the token so the next poll recovers.
      if (e?.response?.status === 401) {
        clearToken();
        setUser(null);
        setEntitlements(null);
        setAccess(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Periodic + focus/visibility re-verification so an EXPIRED subscription
    // kicks the member out of the whole app within ~1 min (backend is the
    // source of truth; this just refreshes the client's access flag).
    const POLL_MS = 60000;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_MS);
    const onFocus = () => refresh();
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    const onStorage = (e) => { if (e.key === 'sm_token' && !e.newValue) { setUser(null); setAccess(false); } };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh]);

  const loginWithToken = useCallback(
    async (token) => {
      setToken(token);
      setLoading(true);
      await refresh();
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      /* ignore */
    }
    clearToken();
    setUser(null);
    setEntitlements(null);
    setAccess(false);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, entitlements, access, loading, refresh, loginWithToken, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
